/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useRef, useState, useCallback } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { UndergroundInfrastructureConfig } from '../../types/underground';

interface UndergroundInfrastructureProps {
  position: [number, number, number];
  config: UndergroundInfrastructureConfig;
  level: number;
  isConnected: boolean;
  onClick: () => void;
  onHover?: (isHovered: boolean) => void;
  utilization?: number; // 0-100% for visual feedback
  status?: 'online' | 'degraded' | 'offline' | 'maintenance';
  layerOpacity?: number;
}

const UndergroundInfrastructure: React.FC<UndergroundInfrastructureProps> = ({
  position,
  config,
  level,
  isConnected,
  onClick,
  onHover,
  utilization = 0,
  status = 'online',
  layerOpacity = 1,
}) => {
  const meshRef = useRef<THREE.Mesh>(null);
  const hoveredRef = useRef(false);
  const [hovered, setHovered] = useState(false);

  // Animation for active infrastructure — never call setState inside useFrame (React #185)
  useFrame((state) => {
    if (!meshRef.current) return;

    if (config.visual.animated) {
      const phase = state.clock.elapsedTime * 2;
      const pulseScale = 1 + Math.sin(phase) * 0.02;
      meshRef.current.scale.setScalar(pulseScale);
    }

    const material = meshRef.current.material as THREE.MeshStandardMaterial | undefined;
    if (!material) return;

    if (status === 'degraded') {
      material.emissive.setHex(0xff6600);
      material.emissiveIntensity = 0.3;
    } else if (status === 'offline') {
      material.emissive.setHex(0xff0000);
      material.emissiveIntensity = 0.2;
    } else if (config.visual.emissive) {
      material.emissive.set(config.visual.color);
      material.emissiveIntensity = hoveredRef.current ? 0.5 : 0.2;
    }
  });

  const handleClick = useCallback((event: THREE.Event) => {
    event.stopPropagation();
    onClick();
  }, [onClick]);

  const handlePointerOver = useCallback((event: THREE.Event) => {
    event.stopPropagation();
    hoveredRef.current = true;
    setHovered(true);
    onHover?.(true);
  }, [onHover]);

  const handlePointerOut = useCallback((event: THREE.Event) => {
    event.stopPropagation();
    hoveredRef.current = false;
    setHovered(false);
    onHover?.(false);
  }, [onHover]);

  // Calculate color based on utilization
  const getUtilizationColor = () => {
    if (utilization > 80) return '#ef4444'; // Red - high utilization
    if (utilization > 60) return '#f59e0b'; // Orange - medium utilization
    if (utilization > 40) return '#eab308'; // Yellow - moderate utilization
    return '#22c55e'; // Green - low utilization
  };

  // Determine base color based on connection status
  const getBaseColor = () => {
    if (!isConnected) return '#6b7280'; // Gray - disconnected
    if (utilization > 0) return getUtilizationColor();
    return config.visual.color;
  };

  return (
    <group position={position}>
      {/* Main infrastructure mesh */}
      <mesh
        ref={meshRef}
        onClick={handleClick}
        onPointerOver={handlePointerOver}
        onPointerOut={handlePointerOut}
        scale={[1, level, 1]}
        castShadow
        receiveShadow
      >
        <boxGeometry
          args={[
            config.dimensions.width,
            config.dimensions.height,
            config.dimensions.depth
          ]}
        />
        <meshStandardMaterial
          color={getBaseColor()}
          metalness={0.8}
          roughness={0.2}
          transparent={!isConnected || hovered || layerOpacity < 1}
          opacity={(isConnected ? (hovered ? 0.8 : 0.95) : 0.3) * layerOpacity}
          emissive={config.visual.emissive ? new THREE.Color(config.visual.color) : '#000000'}
          emissiveIntensity={hovered ? 0.3 : (config.visual.emissive ? 0.1 : 0)}
        />
      </mesh>

      {/* Connection indicators */}
      {isConnected && (
        <>
          {/* Data connection indicator */}
          <mesh position={[0, config.dimensions.height / 2 + 0.1, 0]}>
            <sphereGeometry args={[0.1, 8, 8]} />
            <meshBasicMaterial color="#00ff00" />
          </mesh>

          {/* Power connection indicator */}
          <mesh position={[0, -config.dimensions.height / 2 - 0.1, 0]}>
            <sphereGeometry args={[0.1, 8, 8]} />
            <meshBasicMaterial color="#ffaa00" />
          </mesh>
        </>
      )}

      {/* Utilization bar for network infrastructure */}
      {config.specifications.dataCapacity > 0 && utilization > 0 && (
        <group position={[0, config.dimensions.height + 0.5, 0]}>
          {/* Background bar */}
          <mesh>
            <boxGeometry args={[config.dimensions.width * 0.8, 0.1, 0.1]} />
            <meshBasicMaterial color="#374151" />
          </mesh>

          {/* Utilization fill */}
          <mesh
            position={[-(config.dimensions.width * 0.8 * (1 - utilization / 100)) / 2, 0, 0]}
            scale={[utilization / 100, 1, 1]}
          >
            <boxGeometry args={[config.dimensions.width * 0.8, 0.1, 0.1]} />
            <meshBasicMaterial color={getUtilizationColor()} />
          </mesh>
        </group>
      )}

      {/* Heat dissipation effect for high-heat equipment */}
      {config.specifications.heatOutput > 5 && (
        <HeatDissipationEffect
          position={[0, config.dimensions.height / 2, 0]}
          intensity={config.specifications.heatOutput / 20}
        />
      )}

      {/* Status indicator lights */}
      <group position={[config.dimensions.width / 2, config.dimensions.height, 0]}>
        <StatusLight status={status} />
      </group>
    </group>
  );
};

// Heat dissipation visualization component
const HeatDissipationEffect: React.FC<{
  position: [number, number, number];
  intensity: number;
}> = ({ position, intensity }) => {
  const particlesRef = useRef<THREE.Points>(null);
  const particleCount = Math.floor(20 * intensity);

  useFrame((state) => {
    if (particlesRef.current) {
      const positions = particlesRef.current.geometry.attributes.position.array as Float32Array;

      for (let i = 0; i < particleCount; i++) {
        const i3 = i * 3;
        // Animate particles rising and dispersing
        positions[i3 + 1] += 0.02 * intensity; // Y velocity (rising)
        positions[i3] += (Math.random() - 0.5) * 0.01; // X dispersion
        positions[i3 + 2] += (Math.random() - 0.5) * 0.01; // Z dispersion

        // Reset particles that rise too high
        if (positions[i3 + 1] > 2) {
          positions[i3 + 1] = 0;
          positions[i3] = (Math.random() - 0.5) * 0.5;
          positions[i3 + 2] = (Math.random() - 0.5) * 0.5;
        }
      }

      particlesRef.current.geometry.attributes.position.needsUpdate = true;
    }
  });

  const particles = React.useMemo(() => {
    const positions = new Float32Array(particleCount * 3);

    for (let i = 0; i < particleCount; i++) {
      const i3 = i * 3;
      positions[i3] = (Math.random() - 0.5) * 0.5;     // X
      positions[i3 + 1] = Math.random() * 0.5;          // Y
      positions[i3 + 2] = (Math.random() - 0.5) * 0.5; // Z
    }

    return positions;
  }, [particleCount]);

  return (
    <points ref={particlesRef} position={position}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={particleCount}
          array={particles}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        color="#ff6600"
        size={0.05}
        transparent
        opacity={0.6}
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
};

// Status indicator light component
const StatusLight: React.FC<{ status: 'online' | 'degraded' | 'offline' | 'maintenance' }> = ({ status }) => {
  const getColor = () => {
    switch (status) {
      case 'online': return '#00ff00';
      case 'degraded': return '#ffaa00';
      case 'offline': return '#ff0000';
      case 'maintenance': return '#0080ff';
      default: return '#ffffff';
    }
  };

  const getEmissiveIntensity = () => {
    switch (status) {
      case 'online': return 1.0;
      case 'degraded': return 0.5;
      case 'offline': return 0.2;
      case 'maintenance': return 0.8;
      default: return 0.5;
    }
  };

  return (
    <mesh>
      <sphereGeometry args={[0.15, 16, 16]} />
      <meshStandardMaterial
        color={getColor()}
        emissive={getColor()}
        emissiveIntensity={getEmissiveIntensity()}
        toneMapped={false}
      />
    </mesh>
  );
};

export default UndergroundInfrastructure;